import React, {useEffect, useRef} from 'react';
import {Col, Row} from 'react-bootstrap';
import {DateTime} from 'luxon';
import echarts from '@/utils/echartsCore';
import i18n from 'i18n';

export interface UserAuditSearchChartProps {
    dayKeys: string[];
    axisLabels: string[];
    searchCounts: number[];
    avgQueryDurationSeconds: Array<number | null>;
    avgQueryExecutionSeconds: Array<number | null>;
}

const CHART_HEIGHT_PX = 280;
const TARGET_AXIS_LABELS = 12;

function tooltipDayTitle(dayKey: string): string {
    try {
        return DateTime.fromISO(dayKey, {zone: 'utc'}).toLocaleString(DateTime.DATE_MED);
    } catch {
        return dayKey;
    }
}

function formatSecondsLabel(sec: number | null): string {
    if (sec == null || Number.isNaN(sec)) {
        return '—';
    }
    if (sec < 60) {
        return `${sec.toFixed(2)}s`;
    }
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s.toFixed(1)}s`;
}

const UserAuditSearchChart: React.FC<UserAuditSearchChartProps> = ({
    dayKeys,
    axisLabels,
    searchCounts,
    avgQueryDurationSeconds,
    avgQueryExecutionSeconds,
}) => {
    const countRef = useRef<HTMLDivElement>(null);
    const durationRef = useRef<HTMLDivElement>(null);

    const hasTimingSamples =
        avgQueryDurationSeconds.some((v) => v != null && !Number.isNaN(v))
        || avgQueryExecutionSeconds.some((v) => v != null && !Number.isNaN(v));

    useEffect(() => {
        const el = countRef.current;
        if (!el) {
            return;
        }

        const chart = echarts.init(el);

        const handleResize = () => {
            if (!chart.isDisposed()) {
                chart.resize();
            }
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(el);
        }
        window.addEventListener('resize', handleResize);

        const dayCount = axisLabels.length;
        const labelInterval = Math.max(0, Math.ceil(dayCount / TARGET_AXIS_LABELS) - 1);
        const rotate = dayCount > 10 ? 28 : 0;

        const axisLabel = {
            rotate,
            interval: labelInterval,
            hideOverlap: true,
        };

        const tooltipAxis = {
            trigger: 'axis' as const,
            axisPointer: {type: 'shadow' as const},
            formatter: (params: Array<{dataIndex: number; marker: string; seriesName: string; value: number}>) => {
                if (!params?.length) {
                    return '';
                }
                const idx = params[0].dataIndex;
                const title = tooltipDayTitle(dayKeys[idx] ?? '');
                const lines = params.map(
                    (p) => `${p.marker} ${p.seriesName}: <strong>${p.value}</strong>`,
                );
                return `${title}<br/>${lines.join('<br/>')}`;
            },
        };

        chart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: tooltipAxis,
                grid: {left: 44, right: 12, top: 12, bottom: rotate ? 48 : 24},
                xAxis: {
                    type: 'category',
                    data: axisLabels,
                    axisLabel,
                },
                yAxis: {type: 'value', minInterval: 1},
                series: [
                    {
                        name: i18n.t('ub_chart_searches_series.text'),
                        type: 'bar',
                        data: searchCounts,
                    },
                ],
            },
            true,
        );

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
            chart.dispose();
        };
    }, [dayKeys, axisLabels, searchCounts]);

    useEffect(() => {
        const el = durationRef.current;
        if (!el || !hasTimingSamples) {
            return;
        }

        const chart = echarts.init(el);

        const handleResize = () => {
            if (!chart.isDisposed()) {
                chart.resize();
            }
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(el);
        }
        window.addEventListener('resize', handleResize);

        const dayCount = axisLabels.length;
        const labelInterval = Math.max(0, Math.ceil(dayCount / TARGET_AXIS_LABELS) - 1);
        const rotate = dayCount > 10 ? 28 : 0;

        const axisLabel = {
            rotate,
            interval: labelInterval,
            hideOverlap: true,
        };

        const wallName = i18n.t('ub_chart_search_wall_time_series.text');
        const esName = i18n.t('ub_chart_search_es_time_series.text');

        const tooltipDuration = {
            trigger: 'axis' as const,
            axisPointer: {type: 'shadow' as const},
            formatter: (params: Array<{dataIndex: number; marker: string; seriesName: string; value: number}>) => {
                if (!params?.length) {
                    return '';
                }
                const idx = params[0].dataIndex;
                const title = tooltipDayTitle(dayKeys[idx] ?? '');
                const lines = params.map((p) => {
                    const sec =
                        p.seriesName === wallName
                            ? avgQueryDurationSeconds[idx] ?? null
                            : avgQueryExecutionSeconds[idx] ?? null;
                    const human = formatSecondsLabel(sec);
                    return `${p.marker} ${p.seriesName}: <strong>${human}</strong>`;
                });
                return `${title}<br/>${lines.join('<br/>')}`;
            },
        };

        const wallData = avgQueryDurationSeconds.map((v) =>
            (v != null && !Number.isNaN(v) ? v : null));
        const esData = avgQueryExecutionSeconds.map((v) =>
            (v != null && !Number.isNaN(v) ? v : null));

        chart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: tooltipDuration,
                legend: {top: 0},
                grid: {left: 52, right: 12, top: 32, bottom: rotate ? 48 : 24},
                xAxis: {
                    type: 'category',
                    data: axisLabels,
                    axisLabel,
                },
                yAxis: {
                    type: 'value',
                    scale: true,
                    axisLabel: {
                        formatter: (v: number) => (v >= 60 ? `${(v / 60).toFixed(1)}m` : `${v}s`),
                    },
                },
                series: [
                    {
                        name: wallName,
                        type: 'bar',
                        data: wallData,
                    },
                    {
                        name: esName,
                        type: 'bar',
                        data: esData,
                    },
                ],
            },
            true,
        );

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
            chart.dispose();
        };
    }, [
        dayKeys,
        axisLabels,
        avgQueryDurationSeconds,
        avgQueryExecutionSeconds,
        hasTimingSamples,
    ]);

    return (
        <Row className="g-3 mb-1">
            <Col md={hasTimingSamples ? 6 : 12}>
                <h6 className="mb-2">{i18n.t('ub_chart_searches_per_day.text')}</h6>
                <div ref={countRef} style={{height: CHART_HEIGHT_PX, width: '100%'}} />
            </Col>
            {hasTimingSamples ? (
                <Col md={6}>
                    <h6 className="mb-2">{i18n.t('ub_chart_search_duration_per_day.text')}</h6>
                    <div ref={durationRef} style={{height: CHART_HEIGHT_PX, width: '100%'}} />
                </Col>
            ) : (
                <Col md={6} className="d-flex align-items-center">
                    <p className="small text-muted mb-0">{i18n.t('ub_search_no_timing_chart.text')}</p>
                </Col>
            )}
        </Row>
    );
};

export default UserAuditSearchChart;
